import { useEffect, useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button, isTouchDevice, TextField } from "@shapr3d/frontend-lib";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { z } from "zod";

import { useUpdateTeamSpaceName } from "../../hooks/useTeamSpaces";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import { Modal } from "../../lib/modal";
import { ElementClickAction, ElementClickLocation } from "../../lib/TrackingAdapter";

import style from "./SpaceNameChangeModal.module.css";

type SpaceNameChangeFormData = { name: string };

export default function SpaceNameChangeModal({
  open = true,
  onClose,
  name,
  spaceID,
}: {
  open?: boolean;
  onClose: () => void;
  name?: string;
  spaceID: string;
}) {
  const tracker = useTracker();

  const { teamID } = useParams();
  const { t, m } = useTranslation();
  const schema = useMemo(
    () =>
      z.object({
        name: z.string().nonempty(t(m.TeamSelector.PleaseEnterSpaceName)),
      }),
    [t, m]
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<SpaceNameChangeFormData>({
    resolver: zodResolver(schema),
    defaultValues: { name },
  });

  useEffect(() => {
    if (open) {
      reset({ name });
    }
  }, [open, name, reset]);
  const { mutateAsync, error } = useUpdateTeamSpaceName(teamID);

  const submit = async (data: SpaceNameChangeFormData) => {
    if (!teamID) {
      return;
    }
    try {
      await mutateAsync({ teamID, spaceID, name: data.name });
      onClose();
      tracker.trackButtonClick({
        action: ElementClickAction.CONFIRM,
        location: ElementClickLocation.SPACE_RENAME_MODAL,
      });
    } catch (error) {
      // This is to not throw Unhandled Promise Rejection error
    }
  };

  let errorMessage = null;
  if (error) {
    errorMessage = t(m.ErrorMessage.Unknown);
  }

  return (
    <Modal
      open={open}
      title={t(m.TeamSelector.RenameSpace)}
      size="medium"
      onClose={onClose}
      telemetryName="space_name_change"
    >
      <form className={style.teamNameChangeForm} onSubmit={handleSubmit(submit)}>
        <TextField
          {...register("name")}
          placeholder={t(m.TeamSelector.SpaceNamePlaceholder)}
          errorMessage={errors.name?.message}
          maxLength={200}
          disabled={isSubmitting}
          autoFocus={!isTouchDevice()}
        ></TextField>

        {errorMessage && <div className={style.error}>{errorMessage}</div>}
        <div className={style.buttonContainer}>
          <Button type="submit" disabled={isSubmitting}>
            {t(m.TeamSelector.Save)}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
